import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";

export function Eyebrow({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] ${className}`}>
      <span className="h-2 w-2 rounded-full bg-nead-pink" />
      {children}
    </span>
  );
}

// Pill button. variants: pink / cyan / dark / outline / light
export function Button({ to, href, onClick, children, variant = "pink", className = "", type, disabled, testId }) {
  const styles = {
    pink: "bg-nead-pink text-white hover:bg-nead-navy",
    cyan: "bg-nead-cyan text-white hover:bg-nead-navy",
    dark: "bg-nead-navy text-white hover:bg-nead-pink",
    outline: "border-2 border-nead-navy/20 text-nead-navy hover:border-nead-pink hover:text-nead-pink",
    light: "bg-white text-nead-navy hover:bg-nead-pink hover:text-white",
    "outline-light": "border-2 border-white/30 text-white hover:bg-white hover:text-nead-navy",
  };
  const inner = (
    <span className="flex items-center gap-2">
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </span>
  );
  const base = `group inline-flex items-center rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 disabled:opacity-60 ${styles[variant]} ${className}`;
  if (to) return <Link to={to} className={base} data-testid={testId}>{inner}</Link>;
  if (href) return <a href={href} className={base} data-testid={testId}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} disabled={disabled} className={base} data-testid={testId}>{inner}</button>;
}

export function TextLink({ to, children, testId, className = "" }) {
  return (
    <Link to={to} data-testid={testId} className={`group inline-flex items-center gap-2 font-semibold text-nead-pink ${className}`}>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  );
}

export function RollingNumber({ value, prefix = "", suffix = "", className = "", duration = 1900 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
      else setVal(value);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref} className={`tabular-nums ${className}`}>
      {prefix}{val.toLocaleString()}{suffix}
    </span>
  );
}
